import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useFacilityUser } from "../../hooks/useFacilityUser";
import { paymentApi } from "../../services/payment.api";
import {
  PAYMENT_SESSION_KEY,
  PaymentSessionContext,
  PaymentStatusResponse,
} from "../../types/payment";
import { findOpenPaymentContext } from "../../utils/payment.utils";

type PageState =
  | { kind: "cancelling" }
  | { kind: "cancelled" }
  | { kind: "completed"; response: PaymentStatusResponse };

function readStoredContext(): PaymentSessionContext | null {
  const raw = sessionStorage.getItem(PAYMENT_SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as PaymentSessionContext;
  } catch {
    return null;
  }
}

export const PaymentCancelledPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const facilityId = useFacilityUser()?.healthcareFacility?.id;
  const [state, setState] = useState<PageState>({ kind: "cancelling" });

  // MED-1321: PayDunya sends the manager here after they abandon the checkout.
  // Cancel that payment server-side before confirming it, so the invoice is not
  // left with an open checkout and a new one can be started. Errors are
  // non-fatal: the cancelled message is shown regardless.
  useEffect(() => {
    let active = true;

    const cancelAbandonedPayment = async (): Promise<PaymentStatusResponse | null> => {
      try {
        const ctx = readStoredContext() ?? (await findOpenPaymentContext(facilityId));
        if (!ctx) return null;
        return await paymentApi.cancelInvoicePayment(ctx.invoiceId, ctx.paymentIntentId);
      } catch {
        return null;
      } finally {
        sessionStorage.removeItem(PAYMENT_SESSION_KEY);
      }
    };

    void cancelAbandonedPayment().then((response) => {
      if (!active) return;
      // The payment went through on PayDunya after all — don't claim it was cancelled.
      setState(
        response?.status === "COMPLETED"
          ? { kind: "completed", response }
          : { kind: "cancelled" }
      );
    });

    return () => {
      active = false;
    };
  }, [facilityId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 p-4">
      <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-sm">
        {state.kind === "cancelling" && (
          <>
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
            <p className="mt-4 text-sm text-neutral-600">{t("payment.callback.loading")}</p>
          </>
        )}

        {state.kind === "completed" && (
          <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-100">
              <span className="text-2xl">✓</span>
            </div>
            <h2 className="text-xl font-bold text-success-700">
              {t("payment.callback.success.title")}
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              {t("payment.callback.success.message", {
                amount: state.response.amount.toLocaleString(),
                currency: state.response.currency,
              })}
            </p>
            <button
              onClick={() => navigate("/manager/licence")}
              className="mt-6 w-full rounded-lg bg-primary-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700"
            >
              {t("payment.callback.success.cta")}
            </button>
          </>
        )}

        {state.kind === "cancelled" && (
          <>
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100">
              <span className="text-2xl text-orange-500">✕</span>
            </div>
            <h2 className="text-xl font-bold text-neutral-800">
              {t("payment.cancelled.title")}
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              {t("payment.cancelled.message")}
            </p>
            <button
              onClick={() => navigate("/manager/licence")}
              className="mt-6 w-full rounded-lg bg-primary-600 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700"
            >
              {t("payment.cancelled.cta")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
